import {getJwtExpiration} from './jwt';
import store from 'app/store';

type LogoutReason =
  | 'idle-timeout'
  | 'jwt-expired'
  | 'unauthorized'
  | 'manual'
  | 'session-invalid';

const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;
const SESSION_CHECK_INTERVAL_MS = 15 * 1000;
const MOUSEMOVE_THROTTLE_MS = 1000;
const LOGOUT_ACTION = 'LOGOUT';
const REMOVE_ALL_NOTIFICATION_ACTION = 'REMOVE_ALL_NOTIFICATION';

let lastActivityTimestamp = 0;
let lastMouseMoveTimestamp = 0;
let checkIntervalId: number | null = null;
let listenersAttached = false;
let trackingConfigured = false;
let logoutInProgress = false;

const activityEvents: ReadonlyArray<keyof WindowEventMap> = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
];

function parseStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

function getUserToken(user: any): string {
  return user?.token ?? user?.Token ?? '';
}

function getIdleTimeoutMinutes(user: any): number {
  const rawValue = Number(
    user?.idleTimeoutMinutes ??
      user?.IdleTimeoutMinutes ??
      DEFAULT_IDLE_TIMEOUT_MINUTES,
  );
  return rawValue > 0 ? rawValue : DEFAULT_IDLE_TIMEOUT_MINUTES;
}

function dispatchLogoutState() {
  store.dispatch({type: LOGOUT_ACTION});
  store.dispatch({type: REMOVE_ALL_NOTIFICATION_ACTION});
}

function clearAuthStorage() {
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('requirePasswordChange');
}

function navigateToLoginWithoutReload() {
  if (window.location.pathname.toLowerCase() === '/login') return;

  try {
    window.history.replaceState(window.history.state, '', '/login');
    window.dispatchEvent(new PopStateEvent('popstate'));
  } catch {
    // As a last resort keep behavior deterministic.
    window.location.href = '/login';
  }
}

function updateLastActivity(timestamp: number) {
  if (timestamp <= 0) return;
  if (timestamp <= lastActivityTimestamp) return;

  lastActivityTimestamp = timestamp;
}

export function registerSessionActivity(source = 'manual') {
  void source;
  if (!trackingConfigured) return;
  updateLastActivity(Date.now());
}

function getSessionLogoutReason(user: any): LogoutReason | null {
  const token = getUserToken(user);
  if (!user || !token) return 'session-invalid';

  const idleTimeoutMs = getIdleTimeoutMinutes(user) * 60 * 1000;
  if (lastActivityTimestamp <= 0) {
    updateLastActivity(Date.now());
  }

  if (Date.now() - lastActivityTimestamp >= idleTimeoutMs) {
    return 'idle-timeout';
  }

  const expirationDate = getJwtExpiration(token);
  if (expirationDate && expirationDate <= new Date()) {
    return 'jwt-expired';
  }

  return null;
}

function performLogout(
  reason: LogoutReason,
  options: {redirectToLogin?: boolean} = {},
) {
  if (logoutInProgress) return;
  void reason;

  logoutInProgress = true;

  stopActivityTracking();
  clearAuthStorage();

  dispatchLogoutState();

  if (options.redirectToLogin) {
    navigateToLoginWithoutReload();
  }

  logoutInProgress = false;
}

function onVisibilityChange() {
  if (document.visibilityState !== 'visible') return;

  registerSessionActivity('visibilitychange');
  ensureSessionOrRedirect();
}

function onActivityEvent(event: Event) {
  if (event.type === 'mousemove') {
    const now = Date.now();
    if (now - lastMouseMoveTimestamp < MOUSEMOVE_THROTTLE_MS) {
      return;
    }
    lastMouseMoveTimestamp = now;
  }

  registerSessionActivity(event.type);
}

function attachListeners() {
  if (listenersAttached) return;

  activityEvents.forEach((eventName) => {
    window.addEventListener(eventName, onActivityEvent, {passive: true});
  });
  document.addEventListener('visibilitychange', onVisibilityChange);

  listenersAttached = true;
}

function detachListeners() {
  if (!listenersAttached) return;

  activityEvents.forEach((eventName) => {
    window.removeEventListener(eventName, onActivityEvent);
  });
  document.removeEventListener('visibilitychange', onVisibilityChange);

  listenersAttached = false;
}

function ensureSessionInterval() {
  if (checkIntervalId !== null) return;

  checkIntervalId = window.setInterval(() => {
    if (!trackingConfigured) return;
    ensureSessionOrRedirect();
  }, SESSION_CHECK_INTERVAL_MS);
}

function clearSessionInterval() {
  if (checkIntervalId === null) return;

  window.clearInterval(checkIntervalId);
  checkIntervalId = null;
}

export function isSessionExpired(): boolean {
  return getSessionLogoutReason(parseStoredUser()) !== null;
}

export function ensureSessionOrRedirect(): boolean {
  const reason = getSessionLogoutReason(parseStoredUser());
  if (!reason) {
    return true;
  }

  performLogout(reason, {
    redirectToLogin: true,
  });

  return false;
}

export function configureActivityTracking(user?: any) {
  const activeUser =
    user && getUserToken(user) ? user : (parseStoredUser() as any);

  if (!activeUser || !getUserToken(activeUser)) {
    stopActivityTracking();
    return false;
  }

  trackingConfigured = true;
  updateLastActivity(Date.now());

  attachListeners();
  ensureSessionInterval();
  ensureSessionOrRedirect();

  return true;
}

export function stopActivityTracking() {
  trackingConfigured = false;
  lastActivityTimestamp = 0;
  lastMouseMoveTimestamp = 0;

  detachListeners();
  clearSessionInterval();

  return true;
}

export function logoutSession(
  reason: LogoutReason = 'manual',
  redirectToLogin = false,
) {
  performLogout(reason, {
    redirectToLogin,
  });
}
